import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { captureError } from '@shared/analytics/capture';

type Props = { children: ReactNode };
type State = { failed: boolean };

export function AppErrorFallback(): ReactNode {
  return (
    <main className="bg-aq-bg text-aq-ink flex min-h-screen items-center justify-center p-6">
      <section
        role="alert"
        className="border-aq-border bg-aq-surface w-full max-w-md rounded-xl border p-6 text-center shadow-sm"
      >
        <h1 className="text-aq-lg font-bold">We couldn&apos;t load this page</h1>
        <p className="text-aq-ink-muted text-aq-sm mt-2">
          The app may have updated in the background. Reload to get the latest version.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="bg-aq-accent text-aq-on-accent mt-5 rounded-md px-4 py-2 text-sm font-semibold"
        >
          Reload
        </button>
      </section>
    </main>
  );
}

/** Router `errorElement`: loader, action and lazy-route failures land here
 * without passing through a React error boundary. */
export function RouteErrorFallback(): ReactNode {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (!notFound) captureError(error, { boundary: 'router' });
  }, [error, notFound]);

  return <AppErrorFallback />;
}

/** Last-resort recovery UI for render and lazy-route failures. */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Application render failed', error, info);
    captureError(error, { boundary: 'app' });
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return <AppErrorFallback />;
  }
}
